import { useEffect, useState } from "react";
import { api, formatCents, formatDate } from "@/lib/api";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select, Textarea, Stat } from "@/components/ui/Primitives";
import { downloadFile } from "@/lib/download";
import { unwrap } from "@/lib/unwrap";
import { TrendingUp, DollarSign, Wallet, FileText, Plus, BarChart3, PieChart } from "lucide-react";
import SupportStrip from "@/components/SupportStrip";
import { toast } from "sonner";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

export default function Investor() {
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [docs, setDocs] = useState([]);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    investment_type: "lp", fund_name: "", capital_committed: "",
    capital_contributed: "", current_value: "", ownership_pct: 0, target_irr_pct: 0
  });

  const load = async () => {
    setLoading(true);
    try {
      const [d, p, dist, doc] = await Promise.all([
        api.get("/investor/dashboard").catch(() => ({ data: null })),
        api.get("/investor/portfolio").catch(() => ({ data: null })),
        api.get("/investor/distributions").catch(() => ({ data: [] })),
        api.get("/investor/documents").catch(() => ({ data: [] })),
      ]);
      const k = d.data?.kpis || d.data || {};
      setDash({
        ...d.data,
        contributed_cents: k.total_contributed_cents ?? d.data?.contributed_cents ?? 0,
        committed_cents: k.total_committed_cents ?? d.data?.committed_cents ?? 0,
        current_value_cents: k.current_value_cents ?? d.data?.current_value_cents ?? 0,
        distributions_cents: k.all_time_distributions_cents ?? d.data?.distributions_cents ?? 0,
      });
      setInvestments(unwrap(p.data, "investments"));
      setDistributions(unwrap(dist.data, "distributions"));
      setDocs(unwrap(doc.data, "documents"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadTax = async (form) => {
    try {
      await downloadFile(`/investor/tax-export/${form}?year=${year}`, `${form.toUpperCase()}-${year}.csv`);
      toast.success(`${form.toUpperCase()} ${year} downloaded`);
    } catch (e) {
      const status = e?.status || e?.response?.status;
      if (status === 401 || status === 403) toast.error("Only investor-role accounts can export these forms.");
      else if (status === 402) toast.error("This export requires an active subscription.");
      else toast.error(e?.message || "Download failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/investor/investments", {
        investment_type: form.investment_type,
        fund_name: form.fund_name || null,
        capital_committed_cents: Math.round(Number(form.capital_committed || 0) * 100),
        capital_contributed_cents: Math.round(Number(form.capital_contributed || 0) * 100),
        current_value_cents: Math.round(Number(form.current_value || 0) * 100),
        ownership_pct: Number(form.ownership_pct) || 0,
        target_irr_pct: Number(form.target_irr_pct) || 0,
      });
      toast.success("Investment added");
      setOpen(false);
      setForm({ investment_type: "lp", fund_name: "", capital_committed: "", capital_contributed: "", current_value: "", ownership_pct: 0, target_irr_pct: 0 });
      load();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) toast.error("Only investor-role accounts can add investments.");
      else toast.error(err?.response?.data?.detail || "Could not add investment");
    } finally { setSaving(false); }
  };

  if (loading) return <Loader/>;

  const tabs = [
    { key: "dashboard", label: "Overview" },
    { key: "investments", label: "Investments" },
    { key: "distributions", label: "Distributions" },
    { key: "documents", label: "Documents" },
  ];

  return (
    <div>
      <PageHeader
        title="Investor portal"
        subtitle="Track investments, distributions, and tax documents."
        actions={
          <>
            <Select value={year} onChange={(e) => setYear(e.target.value)} className="w-24" testid="investor-year-select">
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </Select>
            <Button variant="secondary" onClick={() => downloadTax("t5013")} data-testid="t5013-export">Export T5013</Button>
            <Button variant="secondary" onClick={() => downloadTax("nr4")} data-testid="nr4-export">Export NR4</Button>
          </>
        }
      />

      <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`investor-tab-${t.key}`}
            className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${tab === t.key ? "border-green-800 text-slate-950" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Stat label="Committed capital" value={formatCents(dash?.committed_cents || dash?.total_committed || 0)} icon={Wallet} accent="text-slate-900"/>
            <Stat label="Contributed" value={formatCents(dash?.contributed_cents || dash?.total_contributed || 0)} icon={DollarSign}/>
            <Stat label="Current value" value={formatCents(dash?.current_value_cents || dash?.total_value || 0)} icon={TrendingUp} accent="text-green-800"/>
            <Stat label="Distributions" value={formatCents(dash?.distributions_cents || dash?.total_distributions || 0)} icon={PieChart}/>
          </div>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-5 h-5 text-slate-400"/>
              <h3 className="font-display text-lg font-bold text-slate-900">Portfolio summary</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-5 text-sm">
              <Metric label="Active investments" value={investments.length}/>
              <Metric label="Avg target IRR" value={`${(investments.reduce((a, b) => a + (b.target_irr_pct || 0), 0) / Math.max(investments.length, 1)).toFixed(1)}%`}/>
              <Metric label="Total distributions" value={distributions.length}/>
            </div>
          </Card>
        </>
      )}

      {tab === "investments" && (
        <div>
          <div className="flex items-center justify-end mb-4">
            <Button onClick={() => setOpen(true)} data-testid="add-investment-button"><Plus className="w-4 h-4"/> Add investment</Button>
          </div>
          {investments.length === 0 ? (
            <EmptyState icon={Wallet} title="No investments yet" description="Track your real estate partnerships, funds, and direct investments."/>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {investments.map((inv) => (
                <Card key={inv.investment_id} className="p-5" data-testid={`investment-${inv.investment_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-display text-lg font-bold text-slate-900">{inv.fund_name || "—"}</div>
                      <div className="text-xs text-slate-500 mt-0.5 uppercase tracking-wider">{inv.investment_type}</div>
                    </div>
                    <Pill tone="green">{inv.ownership_pct || 0}% owned</Pill>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <Detail label="Committed">{formatCents(inv.capital_committed_cents || 0)}</Detail>
                    <Detail label="Contributed">{formatCents(inv.capital_contributed_cents || 0)}</Detail>
                    <Detail label="Current value">{formatCents(inv.current_value_cents || 0)}</Detail>
                    <Detail label="Target IRR">{(inv.target_irr_pct || 0)}%</Detail>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "distributions" && (
        distributions.length === 0 ? (
          <EmptyState icon={DollarSign} title="No distributions yet" description="Distribution payments from your investments will appear here."/>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr><th className="px-5 py-3">Date</th><th className="px-5 py-3">From</th><th className="px-5 py-3">Type</th><th className="px-5 py-3 text-right">Amount</th></tr>
              </thead>
              <tbody>
                {distributions.map((d, i) => (
                  <tr key={i} className="border-b border-slate-100"><td className="px-5 py-3">{formatDate(d.date || d.created_at)}</td><td className="px-5 py-3 font-semibold">{d.fund_name || d.source}</td><td className="px-5 py-3 capitalize">{d.type || "—"}</td><td className="px-5 py-3 text-right font-semibold">{formatCents(d.amount_cents || d.amount || 0)}</td></tr>
                ))}
              </tbody>
            </table>
          </Card>
        )
      )}

      {tab === "documents" && (
        docs.length === 0 ? (
          <EmptyState icon={FileText} title="No documents" description="Upload K-1s, capital call notices, and operating agreements to your secure vault."/>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {docs.map((d) => (
              <Card key={d.document_id} className="p-4 card-lift">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500"><FileText className="w-5 h-5"/></div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{d.filename || d.title || "Document"}</div>
                    <div className="text-xs text-slate-500">{formatDate(d.created_at)}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add investment" testid="investment-modal">
        <form onSubmit={submit} className="space-y-4">
          <Select label="Type" value={form.investment_type} onChange={(e)=>setForm({...form, investment_type:e.target.value})}>
            <option value="lp">Limited partnership</option>
            <option value="co_owner">Co-owner</option>
            <option value="fund">Fund</option>
            <option value="lender">Lender / mortgage</option>
          </Select>
          <Input label="Fund / project name" required value={form.fund_name} onChange={(e)=>setForm({...form, fund_name:e.target.value})}/>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Committed (CAD)" type="number" value={form.capital_committed} onChange={(e)=>setForm({...form, capital_committed:e.target.value})}/>
            <Input label="Contributed (CAD)" type="number" value={form.capital_contributed} onChange={(e)=>setForm({...form, capital_contributed:e.target.value})}/>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Input label="Current value (CAD)" type="number" value={form.current_value} onChange={(e)=>setForm({...form, current_value:e.target.value})}/>
            <Input label="Ownership %" type="number" step="0.01" value={form.ownership_pct} onChange={(e)=>setForm({...form, ownership_pct:e.target.value})}/>
            <Input label="Target IRR %" type="number" step="0.1" value={form.target_irr_pct} onChange={(e)=>setForm({...form, target_irr_pct:e.target.value})}/>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="investment-submit">{saving ? "Adding…" : "Add"}</Button>
          </div>
        </form>
      </Modal>

      <SupportStrip testid="investor-support-strip"/>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="font-semibold text-slate-900 mt-0.5">{children}</div>
    </div>
  );
}
